import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { UiAvatarComponent } from '@ui';
import { TranslatePipe } from '@ngx-translate/core';
import { ChatMediaBatchesComponent } from '@share-chat';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthActions } from '../../store/state/auth/auth.actions';
import { organizationFeature } from '../../store/state/organization/organization.feature';
import { isOrganizationChatAvailable } from '../../store/state/organization/organization.selectors';
import { userSettingsFeature } from '../../store/state/user-settings/user-settings.feature';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { UserMenuComponent } from '../user-menu/user-menu.component';

@Component({
  selector: 'app-layout',
  imports: [
    RouterOutlet,
    MatSelectModule,
    UiAvatarComponent,
    TranslatePipe,
    SidebarComponent,
    UserMenuComponent,
    ChatMediaBatchesComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject(Store);
  protected readonly organization = this.store$.selectSignal(
    organizationFeature.selectActiveOrganization,
  );
  protected readonly organizations = this.store$.selectSignal(
    organizationFeature.selectUserOrganizations,
  );
  protected readonly availableOrganizations = computed(() =>
    this.organizations().filter(isOrganizationChatAvailable),
  );
  private readonly organizationSwitching = this.store$.selectSignal(
    organizationFeature.selectSwitching,
  );
  protected readonly switchInProgress = (): boolean =>
    this.organizationSwitching();

  public ngOnInit(): void {
    this.store$
      .select(userSettingsFeature.selectTheme)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((theme) => {
        document.body.classList.toggle('dark-theme', theme === 'dark');
        document.body.classList.toggle('light-theme', theme !== 'dark');
      });
  }

  protected switchOrganization(event: MatSelectChange): void {
    const orgId = String(event.value);
    event.source.value = this.organization()?._id;
    if (this.switchInProgress() || orgId === this.organization()?._id) return;
    this.store$.dispatch(
      AuthActions.switchOrganizationRequested({ organizationId: orgId }),
    );
  }
}
